import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router'

import { Button as ButtonV2 } from '@baseV2'
import { TOAST_TYPE } from '@base'
import { DRAWER_TYPES, MODAL_TYPES, SpoStatus, strings } from '@constants'
import { toggleDrawer, toggleModal } from '@data/state/action/root'
import { showLoader, showToast } from '@data/state/action'
// `updateManageSpo` (/oracle/updateManageSpo) is the call this button used to make — it is kept
// imported only so the commented-out line in pushToOracleHandler still points at something real.
// eslint-disable-next-line no-unused-vars
import { updateManageSpo } from '@data/conn/put/oracle'
import { addSpoItemApi, deleteSpoItemApi, pushSpoToOracle, updateSpoItemApi, updateSpoStatus } from '@data/conn/put/SupplyOrder'
import { NavigateBefore, NavigateNext } from '@material-ui/icons'

import { addOrderItemToSpo, createSpo, deleteSpoItem, setSelectedUser, updateNewSpo, updateSpo, updateSpoItem } from '../state/actions'
import ItemsPage from './ItemsPage'
import DetailsPage from './DetailsPage'
import { Page, Body, Footer, FooterRight } from './styles'

// Redesigned 2-page SPO form (dummy route) — handles BOTH create and edit. Reuses the same
// reducer/actions/drawers as the old SPOForm; only the layout changes: no stepper, no side
// message, no FOR/EX on page 1.
const SPOFormV2 = ({ state, dispatch, update, edit }) => {
	const globalDispatch = useDispatch()
	const navigate = useNavigate()
	const loggedInUser = useSelector((state) => state.getIn(['user', 'user']))
	const { spoId } = useParams()
	const { newSpo, selectedUser } = state
	const [page, setPage] = useState(1)

	const valueChangeHandler = (key) => (value) => {
		dispatch(updateNewSpo({ [key]: value }))
	}

	const userChangeHandler = (user) => {
		dispatch(setSelectedUser(user))
	}

	// On an SPO that already exists every item change is saved on its own endpoint; while creating
	// one there is nothing on the server yet, so the item only goes into the local object and is
	// posted with the rest of the SPO on Create.
	const saveItemChange = (request) => {
		if (!edit || !spoId) return
		globalDispatch(showLoader(true))
		request()
			.then(() => update?.())
			.catch((error) => {
				globalDispatch(
					showToast(true, error?.response?.data?.message || strings('error_something_went_wrong'), { type: TOAST_TYPE.ERROR })
				)
			})
			.finally(() => {
				globalDispatch(showLoader(false))
			})
	}

	const addNewItemCallback = (orderItem) => {
		const itemIds = newSpo?.order_items?.reduce((acc, item) => {
			acc.push(item?.item_id)
			return acc
		}, [])
		if (itemIds?.includes(orderItem?.item_id)) {
			globalDispatch(showToast(true, strings('spo_item_already_present'), { type: TOAST_TYPE.ERROR }))
			return
		}
		dispatch(addOrderItemToSpo(orderItem))
		saveItemChange(() => addSpoItemApi(spoId, orderItem))
	}

	const deleteSpoItemCallback = (itemId) => {
		dispatch(deleteSpoItem(itemId))
		saveItemChange(() => deleteSpoItemApi(spoId, itemId))
	}

	const updateSpoItemCallback = (itemId, orderItem) => {
		dispatch(updateSpoItem({ itemId, orderItem }))
		saveItemChange(() => updateSpoItemApi(spoId, itemId, orderItem))
	}

	// Opens the redesigned multi-product Add-Item drawer (v2).
	const addNewItemButtonClick = () => {
		globalDispatch(
			toggleDrawer(true, DRAWER_TYPES.ADD_SPO_ITEMS_MULTI, {
				label: 'Add item details',
				state,
				dispatch,
				addNewItemCallback,
				deleteSpoItemCallback,
				updateSpoItemCallback,
			})
		)
	}

	// Opens the existing shared Other-Charges drawer (reused for "Add Charge").
	const addChargeButtonClick = () => {
		globalDispatch(
			toggleDrawer(true, DRAWER_TYPES.OTHER_CHARGES_PO, {
				label: strings('other', 'charges'),
				edit: true,
				data: newSpo?.other_charges,
				editCallback: (otherCharges) => {
					valueChangeHandler('other_charges')(otherCharges)
					globalDispatch(toggleDrawer(false))
				},
			})
		)
	}

	const itemDeleteHandler = (item) => (e) => {
		e?.stopPropagation?.()
		globalDispatch(
			toggleModal(true, MODAL_TYPES.CONFIRMATION, {
				overlayClickable: true,
				title: strings('msg_are_you_sure_want_to_delete'),
				confirmAction: (actionType) => {
					if (actionType) deleteSpoItemCallback(item?.item_id)
					globalDispatch(toggleModal(false))
				},
			})
		)
	}

	const editItemButtonClick = (item) => {
		globalDispatch(
			toggleDrawer(true, DRAWER_TYPES.EDIT_SPO_ITEM, {
				label: strings('edit', 'spo'),
				edit: true,
				itemId: item?.item_id,
				state,
				deleteSpoItemCallback,
				updateSpoItemCallback,
				dispatch,
			})
		)
	}

	const createSpoHandler = () => {
		if (deliveryDatesMissing) {
			globalDispatch(showToast(true, strings('msg_delivery_dates_required'), { type: TOAST_TYPE.ERROR }))
			return
		}
		dispatch(createSpo(newSpo, globalDispatch, navigate))
	}

	// Order Details drawer — linked orders (Order Id / Input Type / Alloted Weight), same as the old SPO form.
	const orderDetailsHandler = () =>
		globalDispatch(toggleDrawer(true, DRAWER_TYPES.SPO_ORDER_DETAILS, { label: strings('order_details'), data: newSpo }))

	const updateHandler = () => {
		// Both delivery dates are mandatory on an SPO, on create as well as on edit.
		if (deliveryDatesMissing) {
			globalDispatch(showToast(true, strings('msg_delivery_dates_required'), { type: TOAST_TYPE.ERROR }))
			return
		}
		dispatch(updateSpo(newSpo, globalDispatch, update, navigate))
	}

	const pushToOracleHandler = () => {
		if (!spoId) return
		globalDispatch(showLoader(true))
		// updateManageSpo(spoId)
		pushSpoToOracle(spoId)
			.then(() => {
				globalDispatch(showToast(true, 'Pushed to oracle successfully', { type: TOAST_TYPE.SUCCESS }))
				update()
			})
			.catch((error) => {
				globalDispatch(showToast(true, error?.response?.data?.message || 'Error while pushing to oracle', { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				globalDispatch(showLoader(false))
			})
	}

	const continueDisabled = !newSpo?.supplier_po_date || !selectedUser || !newSpo?.order_items || newSpo?.order_items?.size === 0
	const paymentTermsList = newSpo?.payment_terms_list
	const paymentTermsCount = paymentTermsList?.size ?? paymentTermsList?.length ?? 0
	const deliveryDatesMissing = !newSpo?.dispatch_start_date || !newSpo?.expected_delivery_date
	const createDisabled =
		!newSpo?.terms || !newSpo?.billing_address || !newSpo?.dispatch_location || paymentTermsCount === 0 || deliveryDatesMissing
	const isActive = [SpoStatus.CREATED, SpoStatus.ACTIVE].includes(newSpo?.supplier_po_status)

	const statusChangeHandler = (option) => {
		globalDispatch(
			toggleModal(true, MODAL_TYPES.CONFIRMATION, {
				overlayClickable: true,
				title: strings('want_to_update_spo_status'),
				confirmAction: (actionType) => {
					if (actionType) {
						globalDispatch(showLoader(true))
						updateSpoStatus(newSpo?.supplier_po_number, option?.key, loggedInUser?.user_id)
							.then(() => {
								globalDispatch(showToast(true, strings('msg_spo_status_update_success'), { type: TOAST_TYPE.SUCCESS }))
							})
							.catch(() => {
								globalDispatch(showToast(true, strings('msg_spo_status_update_error'), { type: TOAST_TYPE.ERROR }))
							})
							.finally(() => {
								update()
								globalDispatch(showLoader(false))
							})
					}
					globalDispatch(toggleModal(false))
				},
			})
		)
	}

	return (
		<Page>
			<Body>
				{page === 1 ? (
					<ItemsPage
						state={state}
						edit={edit}
						valueChangeHandler={valueChangeHandler}
						userChangeHandler={userChangeHandler}
						addNewItemButtonClick={addNewItemButtonClick}
						addChargeButtonClick={addChargeButtonClick}
						editItemButtonClick={editItemButtonClick}
						itemDeleteHandler={itemDeleteHandler}
						statusChangeHandler={statusChangeHandler}
					/>
				) : (
					<DetailsPage state={state} valueChangeHandler={valueChangeHandler} edit={edit} />
				)}
			</Body>

			<Footer>
				{page === 2 && (
					<ButtonV2 color='primary' variant='outline'  iconPrefix={<NavigateBefore fontSize='small' />} onClick={() => setPage(1)}>
						{strings('back')}
					</ButtonV2>
				)}
				<FooterRight>
					{edit && (
						<ButtonV2 color='primary' variant='outline' onClick={orderDetailsHandler}>
							{strings('order_details')}
						</ButtonV2>
					)}
					{page === 1 ? (
						<ButtonV2
							color='primary'
							iconSuffix={<NavigateNext fontSize='small' />}
							disabled={continueDisabled}
							onClick={() => setPage(2)}
						>
							{strings('continue')}
						</ButtonV2>
					) : edit ? (
						<>
							<ButtonV2 color='primary' variant='outline'  disabled={!spoId || !isActive} onClick={pushToOracleHandler}>
								{strings('push_to_oracle')}
							</ButtonV2>
							<ButtonV2 color='primary'  disabled={!isActive} onClick={updateHandler}>
								{strings('update')}
							</ButtonV2>
						</>
					) : (
						<ButtonV2 color='primary'  disabled={createDisabled} onClick={createSpoHandler}>
							{strings('create', 'spo')}
						</ButtonV2>
					)}
				</FooterRight>
			</Footer>
		</Page>
	)
}

export default SPOFormV2
